/**
 * @fileoverview Reporting router component with access control.
 */

import React from "react";
import {Route, Navigate} from "react-router-dom";
import {SentryRoutes} from "../utils/sentryRoute";

import {
  PageReportingReportsCheckboxes,
  PageReportingFullReport,
  PageReportingDashboards,
  PageReportingGeneral,
  PageReportingOther,
  PageReportingPermissionsManagement,
  PageReportingPermissionsUserDetail,
  PageReportingPermissionsBulkEdit,
} from "../pages/page-reporting";
import {PageAdmin404} from "../pages/page-admin-404";
import {ConnectedReportContextProvider} from "../pages/page-reporting/ReportContext";
import {AdminHasReportingAccess} from "../models/admin-session/AdminHasReportingAccess";

type ReportingRouterRendererProps = Readonly<{
  basePath: string;
}>;

/**
 * Renderer component for reporting routes.
 * Handles routing for different reporting pages and redirects.
 */
export const ReportingRouterRenderer = ({
  basePath,
}: ReportingRouterRendererProps) => (
  <ConnectedReportContextProvider>
    <SentryRoutes>
      <Route
        index
        element={
          <Navigate
            to={
              basePath === "/admin/permissions"
                ? `${basePath}/management`
                : `${basePath}/general`
            }
            replace
          />
        }
      />
      <Route path="general" element={<PageReportingGeneral />} />
      <Route path="dashboards" element={<PageReportingDashboards />} />
      <Route
        path="checkboxes"
        element={<PageReportingReportsCheckboxes />}
      />
      <Route
        path="management/bulk"
        element={<PageReportingPermissionsBulkEdit />}
      />
      <Route
        path="management/:userId"
        element={<PageReportingPermissionsUserDetail />}
      />
      <Route
        path="management"
        element={<PageReportingPermissionsManagement />}
      />
      <Route path="other" element={<PageReportingOther />} />
      <Route
        path=":type/:chartGroup/:chartId/:from1/:to1"
        element={<PageReportingFullReport />}
      />
      <Route path="*" element={<PageAdmin404 />} />
    </SentryRoutes>
  </ConnectedReportContextProvider>
);

/**
 * Reporting router with access control.
 * Guards the reporting routes to ensure only users with reporting access can view them.
 */
export const ReportingRouter = AdminHasReportingAccess(ReportingRouterRenderer);
